import { forwardRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {
  ToastIconContainer,
  ToastBody,
  ToastContent,
  ToastWrapper,
  TOAST_POSITIONS,
  TOAST_VARIANTS,
  useShareForwardedRef
} from 'cdk-radial';

const LevelToastWrapper = styled(ToastWrapper)`
  z-index: auto;
`;

/**
 * `Notice` displays an informational box resembling an RDS `Toast`.
 *
 * Internally, it is based on `Toast`, but with some key differences:
 * * `Notice` is always inline, and cannot be fixed in the upper-right corner.
 * * `Notice` is always visible while rendered, will not dismiss itself, and
 *   cannot be dismissed by the user.
 * * `Notice` does not accept actions.
 * * `Notice` is rendered at Z-index 0. (`Toast` is usually rendered higher.)
 */
const Notice = forwardRef(
  ({ className, content, id, style, variant, ...other }, ref) => {
    const toastRef = useShareForwardedRef(ref);

    return (
      <LevelToastWrapper
        className={`${className} toast-wrapper-${className}`}
        id={id}
        position={TOAST_POSITIONS.INLINE}
        ref={toastRef}
        style={style}
        variant={variant}
        {...other}
      >
        <ToastIconContainer
          className={`${className} toast-icon-${className}`}
          variant={variant}
        />
        <ToastBody
          className={`${className} toast-body-${className}`}
          variant={variant}
        >
          <ToastContent className={`${className} toast-content-${className}`}>
            {content}
          </ToastContent>
        </ToastBody>
      </LevelToastWrapper>
    );
  }
);

Notice.propTypes = {
  /** Adds classname for additional styling */
  className: PropTypes.string,
  /** The content of the component */
  content: PropTypes.node.isRequired,
  /** Unique id for component */
  id: PropTypes.string.isRequired,
  /** Inline styles applied to main component wrapper */
  style: PropTypes.shape({}),
  /** Changes toast style depending on variant. */
  variant: PropTypes.oneOf(Object.values(TOAST_VARIANTS))
};

Notice.defaultProps = {
  className: undefined,
  style: {},
  variant: TOAST_VARIANTS.NEUTRAL
};

export default Notice;
